import type { LandscapingConfig } from '@paint-ai/shared';
import { Check, Flower2, SlidersHorizontal, Trees } from 'lucide-react';
import { useState } from 'react';
import { Card } from '@/components/ui';
import { cn } from '@/lib/cn';
import { LANDSCAPING_PRESETS, type LandscapingPreset } from '../../data/landscaping';
import { LandscapingFineTune } from './LandscapingFineTune';

interface LandscapingSelectorProps {
  config: LandscapingConfig;
  onChange: (config: LandscapingConfig) => void;
}

export function LandscapingSelector({ config, onChange }: LandscapingSelectorProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const applyPreset = (preset: LandscapingPreset) =>
    onChange({
      preset: preset.id,
      presetName: preset.name,
      plants: [...preset.plants],
      lawnStyle: preset.lawnStyle,
      pathwayStyle: preset.pathwayStyle,
      customNotes: config.customNotes,
    });

  return (
    <Card>
      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
        <div>
          <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Trees className="w-4 h-4 text-emerald-600" />
            Landscaping & Foreground Design
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Customize native Indian flora, lawns, and stone pathways around the property
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          aria-expanded={showAdvanced}
          className="text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          {showAdvanced ? 'Simple View' : 'Customize Flora'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {LANDSCAPING_PRESETS.map((preset) => {
          const selected = config.preset === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              aria-pressed={selected}
              onClick={() => applyPreset(preset)}
              className={cn(
                'p-3.5 rounded-xl border transition-all cursor-pointer relative text-left',
                selected
                  ? 'bg-emerald-50/70 border-emerald-500 ring-1 ring-emerald-500/30'
                  : 'bg-slate-50/70 border-slate-200/80 hover:bg-slate-50 hover:border-slate-300',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                  <Flower2 className={cn('w-3.5 h-3.5', selected ? 'text-emerald-600' : 'text-slate-400')} />
                  {preset.name}
                </span>
                {selected && (
                  <span className="bg-emerald-600 text-white rounded-full p-0.5">
                    <Check className="w-3 h-3 stroke-[3]" />
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">{preset.description}</p>
            </button>
          );
        })}
      </div>

      {showAdvanced && <LandscapingFineTune config={config} onChange={onChange} />}
    </Card>
  );
}
